import { useEffect, useMemo, useState } from "react";
import AuthForm from "../AuthForm";
import "./homepage.css";

const HERO_BULLETS = [
    "AI 拆分复杂目标，生成首批可执行步骤",
    "进度可视化时间轴，随时复盘阻塞点",
    "跨设备同步，随时继续未完成的任务"
];

const FEATURE_LIST = [
    {
        title: "智能拆解",
        description: "输入目标后自动生成阶段、里程碑与建议行动，避免久拖不决。",
        icon: "🎯"
    },
    {
        title: "进度雷达",
        description: "用彩色雷达与燃尽图展示推进速度，让风险提前暴露。",
        icon: "📈"
    },
    {
        title: "复盘助手",
        description: "完成后提醒复盘，沉淀经验并自动归档到个人成就库。",
        icon: "🧠"
    },
    {
        title: "协同提醒",
        description: "将关键节点分享到群组，自动提醒合作者跟进。",
        icon: "🤝"
    }
];

const WORKFLOW_STEPS = [
    {
        label: "设定目标",
        detail: "描述想完成的成果与截止时间，DoneLog 自动识别语义要点。",
        number: "01"
    },
    {
        label: "拆解执行",
        detail: "AI 与模板混合生成任务列表，可拖拽调序并分配协作者。",
        number: "02"
    },
    {
        label: "跟踪复盘",
        detail: "仪表盘实时更新完成度，结束后生成复盘文档与下一步建议。",
        number: "03"
    }
];

const METRICS = [
    { value: "3.2x", label: "平均完成速度提升" },
    { value: "12k+", label: "已归档的目标" },
    { value: "92%", label: "复盘保留率" }
];

const TESTIMONIALS = [
    {
        quote: "以前 OKR 写完就忘，现在每周站会都有数据支撑，团队真正把重点落到了行动上。",
        author: "陈晓晨 · 产品负责人"
    },
    {
        quote: "DoneLog 帮我把考研计划拆成日程，系统提醒我复盘，焦虑感小了很多。",
        author: "刘凯 · 在读研究生"
    },
    {
        quote: "创业项目多线程推进的时候最怕遗漏节点，这里直接看到整体进度和风险。",
        author: "林可 · 初创公司 CEO"
    }
];

const PRICING_PLANS = [
    {
        name: "个人版",
        price: "免费",
        description: "适合个人目标与学习计划",
        features: ["单人使用", "最多 5 个并行目标", "AI 拆解每月 30 次", "完成复盘模板"]
    },
    {
        name: "团队版",
        price: "￥39 / 用户 / 月",
        description: "为团队 OKR 与跨部门项目设计",
        features: ["无限目标与协作者", "进度雷达与风险聚合", "自定义模板与自动化", "企业级安全与审计"]
    }
];

const FAQS = [
    {
        question: "DoneLog 与普通待办工具有什么不同？",
        answer: "待办工具关注日常任务，DoneLog 专注于帮助你把一件复杂的事情做完，从拆解到复盘都有引导。"
    },
    {
        question: "加入内测需要付费吗？",
        answer: "不需要，个人内测完全免费。我们会根据使用反馈不断改进产品，并优先开放新功能。"
    },
    {
        question: "数据会在设备之间同步吗？",
        answer: "会，DoneLog 默认启用云端同步，任何设备登录都可以继续当前的目标与任务。"
    },
    {
        question: "团队版什么时候上线？",
        answer: "团队版计划在 2025 年第二季度公测，内测用户会获得 3 个月的低价试用名额。"
    }
];

export default function HomePage({ onLogin }) {
    const [isAuthOpen, setIsAuthOpen] = useState(false);
    const [waitlistEmail, setWaitlistEmail] = useState("");
    const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);

    useEffect(() => {
        document.body.classList.add("landing-active");
        return () => {
            document.body.classList.remove("landing-active");
        };
    }, []);

    const partnerList = useMemo(
        () => ["Product Hunt", "Notion", "Lark", "Slack", "GitHub"],
        []
    );

    const handleWaitlistSubmit = (event) => {
        event.preventDefault();
        if (!waitlistEmail.trim()) {
            return;
        }

        setWaitlistSubmitted(true);
        setWaitlistEmail("");
        window.setTimeout(() => setWaitlistSubmitted(false), 4000);
    };

    const openAuth = () => setIsAuthOpen(true);
    const closeAuth = () => setIsAuthOpen(false);

    const handleAuthSuccess = (userData, token) => {
        if (onLogin) {
            onLogin(userData, token);
        }
        setIsAuthOpen(false);
    };

    return (
        <div className="home">
            <header className="navbar">
                <div className="navbar__brand">
                    <span className="navbar__logo">DoneLog</span>
                    <span className="navbar__tagline">完成每一件想做的事</span>
                </div>

                <nav className="navbar__links">
                    <a href="#features">功能亮点</a>
                    <a href="#workflow">工作流</a>
                    <a href="#pricing">定价</a>
                    <a href="#faq">常见问题</a>
                </nav>

                <div className="navbar__actions">
                    <button type="button" className="btn ghost" onClick={openAuth}>
                        登录
                    </button>
                    <button type="button" className="btn primary" onClick={openAuth}>
                        免费体验
                    </button>
                </div>
            </header>

            <main className="main">
                <section className="hero" id="top">
                    <div className="hero__content">
                        <span className="hero__badge">Beta 内测招募中</span>
                        <h1>把目标拆解成可以完成的行动</h1>
                        <p className="hero__subtitle">
                            DoneLog 用 AI 和模板帮你拆分目标、对齐优先级、追踪完成进度，确保每一步都走得稳。
                        </p>
                        <div className="hero__bullets">
                            {HERO_BULLETS.map((item) => (
                                <div key={item} className="hero__bullet">
                                    <span className="hero__bullet-dot" />
                                    <span>{item}</span>
                                </div>
                            ))}
                        </div>
                        <div className="hero__cta">
                            <button type="button" className="btn primary" onClick={openAuth}>
                                立即开启
                            </button>
                            <a className="btn secondary" href="#register">
                                加入内测名单
                            </a>
                        </div>
                    </div>

                    <div className="hero__preview" aria-hidden="true">
                        <div className="preview-card">
                            <div className="preview-card__header">
                                <span>OKR 目标：上线新官网</span>
                                <span className="status-pill">进行中 • 68%</span>
                            </div>
                            <ul className="preview-card__list">
                                <li>
                                    <span className="preview-check completed" />完成内容策略确认
                                    <span className="preview-assignee">Design</span>
                                </li>
                                <li>
                                    <span className="preview-check" />迭代 Figma 设计稿
                                    <span className="preview-assignee">UI</span>
                                </li>
                                <li>
                                    <span className="preview-check" />配置部署流水线
                                    <span className="preview-assignee">DevOps</span>
                                </li>
                                <li>
                                    <span className="preview-check" />撰写上线复盘模版
                                    <span className="preview-assignee">PM</span>
                                </li>
                            </ul>
                            <div className="preview-card__footer">
                                <span>下一个里程碑 · 4 月 28 日</span>
                                <button type="button" className="link-button" onClick={openAuth}>
                                    查看详情 →
                                </button>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="metrics" id="metrics">
                    {METRICS.map((metric) => (
                        <div key={metric.label} className="metric-card">
                            <span className="metric-card__value">{metric.value}</span>
                            <span className="metric-card__label">{metric.label}</span>
                        </div>
                    ))}
                </section>

                <section className="partners" aria-label="生态伙伴">
                    <p>与常用工具串联工作流</p>
                    <div className="partners__marquee">
                        {partnerList.map((partner) => (
                            <span key={partner}>{partner}</span>
                        ))}
                    </div>
                </section>

                <section className="features" id="features">
                    <div className="section-heading">
                        <span className="eyebrow">核心能力</span>
                        <h2>围绕“完成”打造的全链路工具</h2>
                        <p>
                            不只是记录任务，而是通过拆解、执行、复盘三个阶段帮你稳定交付。
                        </p>
                    </div>
                    <div className="features__grid">
                        {FEATURE_LIST.map((feature) => (
                            <article key={feature.title} className="feature-card">
                                <div className="feature-card__icon" aria-hidden="true">{feature.icon}</div>
                                <h3>{feature.title}</h3>
                                <p>{feature.description}</p>
                            </article>
                        ))}
                    </div>
                </section>

                <section className="workflow" id="workflow">
                    <div className="section-heading">
                        <span className="eyebrow">工作流</span>
                        <h2>三步完成目标，从立项到复盘</h2>
                    </div>
                    <div className="workflow__grid">
                        {WORKFLOW_STEPS.map((step) => (
                            <article key={step.number} className="workflow-card">
                                <span className="workflow-card__number">{step.number}</span>
                                <h3>{step.label}</h3>
                                <p>{step.detail}</p>
                            </article>
                        ))}
                    </div>
                </section>

                <section className="showcase" aria-label="功能预览">
                    <div className="showcase__panel">
                        <h2>一目了然的项目脉络</h2>
                        <p>
                            时间轴自动串联里程碑、责任人和风险提示。当关键节点临近时，DoneLog 会自动发送提醒，让团队在同一节奏上推进。
                        </p>
                        <ul className="showcase__list">
                            <li>多维视图：看板、日历、燃尽图自由切换</li>
                            <li>智能提醒：风险预测、延误告警、每日进度播报</li>
                            <li>知识沉淀：复盘模板、经验库和标签归档</li>
                        </ul>
                        <button type="button" className="btn secondary" onClick={openAuth}>
                            预约演示
                        </button>
                    </div>
                    <div className="showcase__mock" aria-hidden="true">
                        <div className="mock-window">
                            <div className="mock-window__header">
                                <span className="dot red" />
                                <span className="dot yellow" />
                                <span className="dot green" />
                            </div>
                            <div className="mock-window__body">
                                <div className="mock-timeline">
                                    <div className="mock-timeline__item active">
                                        <span className="mock-timeline__time">周一</span>
                                        <div className="mock-timeline__content">
                                            <strong>启动会议</strong>
                                            <span>完成开题和资源确认</span>
                                        </div>
                                    </div>
                                    <div className="mock-timeline__item">
                                        <span className="mock-timeline__time">周三</span>
                                        <div className="mock-timeline__content">
                                            <strong>开发里程碑</strong>
                                            <span>前端模块进入测试</span>
                                        </div>
                                    </div>
                                    <div className="mock-timeline__item">
                                        <span className="mock-timeline__time">周五</span>
                                        <div className="mock-timeline__content">
                                            <strong>复盘会议</strong>
                                            <span>记录阻塞与改进项</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="mock-summary">
                                    <h4>本周完成度</h4>
                                    <p>68% · 较上周 +12%</p>
                                    <ul>
                                        <li><span className="badge success">完成</span> 12 个任务</li>
                                        <li><span className="badge warning">风险</span> 1 个阻塞待处理</li>
                                        <li><span className="badge info">待确认</span> 2 个里程碑</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="testimonials" aria-label="用户反馈">
                    <div className="section-heading">
                        <span className="eyebrow">真实声音</span>
                        <h2>他们已经把 DoneLog 纳入日常工作</h2>
                    </div>
                    <div className="testimonials__grid">
                        {TESTIMONIALS.map((item) => (
                            <blockquote key={item.author} className="testimonial-card">
                                <p>“{item.quote}”</p>
                                <footer>— {item.author}</footer>
                            </blockquote>
                        ))}
                    </div>
                </section>

                <section className="pricing" id="pricing">
                    <div className="section-heading">
                        <span className="eyebrow">定价计划</span>
                        <h2>先免费体验，再决定是否升级</h2>
                        <p>团队版提供 14 天试用，支持发票与企业级安全要求。</p>
                    </div>
                    <div className="pricing__grid">
                        {PRICING_PLANS.map((plan) => (
                            <article key={plan.name} className="pricing-card">
                                <h3>{plan.name}</h3>
                                <p className="pricing-card__price">{plan.price}</p>
                                <p className="pricing-card__description">{plan.description}</p>
                                <ul>
                                    {plan.features.map((feature) => (
                                        <li key={feature}>{feature}</li>
                                    ))}
                                </ul>
                                <button type="button" className="btn primary" onClick={openAuth}>
                                    开始使用
                                </button>
                            </article>
                        ))}
                    </div>
                </section>

                <section className="faq" id="faq">
                    <div className="section-heading">
                        <span className="eyebrow">FAQ</span>
                        <h2>常见问题</h2>
                    </div>
                    <div className="faq__grid">
                        {FAQS.map((faq) => (
                            <article key={faq.question} className="faq-card">
                                <h3>{faq.question}</h3>
                                <p>{faq.answer}</p>
                            </article>
                        ))}
                    </div>
                </section>

                <section className="register" id="register">
                    <div className="register__card">
                        <h2>加入内测名单，第一时间体验新功能</h2>
                        <p>留下邮箱，我们将在新版本上线或开放团队版时优先通知你。</p>
                        <form className="register__form" onSubmit={handleWaitlistSubmit}>
                            <input
                                type="email"
                                name="email"
                                placeholder="you@example.com"
                                value={waitlistEmail}
                                onChange={(event) => setWaitlistEmail(event.target.value)}
                                required
                            />
                            <button type="submit" className="btn primary">提交</button>
                        </form>
                        {waitlistSubmitted && (
                            <p className="register__success">收到啦！我们会在内测更新时邮件通知你。</p>
                        )}
                    </div>
                </section>

                <section className="cta">
                    <div className="cta__content">
                        <h2>用 DoneLog 把“想完成的事”变成“已经完成”</h2>
                        <p>马上登录，即可体验智能拆解、进度雷达和复盘助手。</p>
                        <div className="cta__actions">
                            <button type="button" className="btn primary" onClick={openAuth}>
                                登录 / 注册
                            </button>
                            <a className="btn secondary" href="mailto:team@donelog.app">
                                预约团队演示
                            </a>
                        </div>
                    </div>
                </section>
            </main>

            <footer className="footer">
                <div className="footer__meta">
                    <span className="navbar__logo">DoneLog</span>
                    <p>完成每一件想做的事</p>
                </div>
                <div className="footer__links">
                    <a href="mailto:team@donelog.app">联系我们</a>
                    <a href="#pricing">定价</a>
                    <a href="#faq">帮助中心</a>
                </div>
                <p className="footer__copy">© {new Date().getFullYear()} DoneLog. 保留所有权利。</p>
            </footer>

            {isAuthOpen && (
                <div className="auth-modal" role="dialog" aria-modal="true">
                    <div className="auth-modal__backdrop" onClick={closeAuth} />
                    <div className="auth-modal__content">
                        <button
                            type="button"
                            className="auth-modal__close"
                            onClick={closeAuth}
                            aria-label="关闭登录窗口"
                        >
                            x
                        </button>
                        <AuthForm onLogin={handleAuthSuccess} />
                    </div>
                </div>
            )}
        </div>
    );
}
